import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("skilmark_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail.map((d) => d?.msg || String(d)).join(", ") || "Request failed";
    }
    return Promise.reject(error);
  }
);

export const fetchMetrics = async () => {
  const res = await axios.get(`${API}/metrics`);
  return res.data.metrics;
};

export const generateCampaign = async (payload) => {
  const res = await axios.post(`${API}/generate-campaign`, payload);
  return res.data;
};

export const generateSequences = async (payload) => {
  const res = await axios.post(`${API}/generate-sequences`, payload);
  return res.data;
};

export const submitLead = async (payload) => {
  const res = await axios.post(`${API}/leads`, payload);
  return res.data;
};

export const fetchApps = async () => {
  const res = await axios.get(`${API}/apps`);
  return res.data;
};

export const createApp = async (payload) => {
  const res = await axios.post(`${API}/apps`, payload);
  return res.data;
};

export const deleteApp = async (appId) => {
  const res = await axios.delete(`${API}/apps/${appId}`);
  return res.data;
};

export const fetchAppCampaigns = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/campaigns`);
  return res.data.campaigns;
};

export const generateCampaignForApp = async (appId, tone, focusFeature) => {
  const res = await axios.post(`${API}/apps/${appId}/generate-campaign`, { tone: tone || undefined, focus_feature: focusFeature || undefined });
  return res.data;
};

export const updateAppFeatures = async (appId, keyFeatures) => {
  const res = await axios.patch(`${API}/apps/${appId}/features`, { key_features: keyFeatures });
  return res.data;
};

export const fetchFeatureRecommendation = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/feature-recommendation`);
  return res.data;
};

export const fetchAppSequences = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/sequences`);
  return res.data.sequences;
};

export const generateSequencesForApp = async (appId) => {
  const res = await axios.post(`${API}/apps/${appId}/generate-sequences`);
  return res.data;
};

export const markWinningHook = async (campaignId) => {
  const res = await axios.post(`${API}/campaigns/${campaignId}/mark-winning`);
  return res.data;
};

export const fetchHooksLeaderboard = async () => {
  const res = await axios.get(`${API}/hooks-leaderboard`);
  return res.data.leaderboard;
};

export const sendWeeklyDigest = async () => {
  const res = await axios.post(`${API}/digest/send-me`);
  return res.data;
};

export const fetchBlogPosts = async (page = 1, { tag, search } = {}) => {
  const res = await axios.get(`${API}/blog`, {
    params: { page, tag: tag || undefined, search: search || undefined },
  });
  return res.data;
};

export const fetchBlogTags = async () => {
  const res = await axios.get(`${API}/blog/tags`);
  return res.data.tags;
};

export const fetchBlogPost = async (slug) => {
  const res = await axios.get(`${API}/blog/${slug}`);
  return res.data;
};

export const fetchWinningHooks = async () => {
  const res = await axios.get(`${API}/winning-hooks`);
  return res.data.hooks;
};

export const applyHookToApp = async (appId, sourceCampaignId) => {
  const res = await axios.post(`${API}/apps/${appId}/apply-hook`, { source_campaign_id: sourceCampaignId });
  return res.data;
};

export const fetchAdminAnalytics = async () => {
  const res = await axios.get(`${API}/admin/analytics`);
  return res.data;
};

export const fetchAdminLeads = async () => {
  const res = await axios.get(`${API}/admin/leads`);
  return res.data.leads;
};

export const adminGrantPlan = async (email, plan) => {
  const res = await axios.post(`${API}/admin/users/grant-plan`, { email, plan });
  return res.data;
};

export const fetchComments = async (slug) => {
  const res = await axios.get(`${API}/blog/${slug}/comments`);
  return res.data.comments;
};

export const postComment = async (slug, payload) => {
  const res = await axios.post(`${API}/blog/${slug}/comments`, payload);
  return res.data;
};

export const trackTagClick = async (tag) => {
  try {
    await axios.post(`${API}/blog/tags/${encodeURIComponent(tag)}/click`);
  } catch {
    // best-effort, ignore failures
  }
};

export const renderCampaignVideo = async (campaignId, voiceId, aiVideoMode, aiScope, captionStyle) => {
  const body = {};
  if (voiceId) body.voice_id = voiceId;
  if (aiVideoMode) {
    body.ai_video_mode = true;
    body.ai_scope = aiScope || "all";
  }
  if (captionStyle) body.caption_style = captionStyle;
  const res = await axios.post(`${API}/campaigns/${campaignId}/render-video`, body);
  return res.data;
};

export const fetchCaptionStyles = async () => {
  const res = await axios.get(`${API}/caption-styles`);
  return res.data.styles;
};

export const fetchRenderCostEstimate = async (campaignId, aiScope) => {
  const res = await axios.get(`${API}/campaigns/${campaignId}/render-cost-estimate`, { params: { ai_scope: aiScope } });
  return res.data;
};

export const retryRenderVideo = async (renderId) => {
  const res = await axios.post(`${API}/renders/${renderId}/retry`);
  return res.data;
};

export const fetchRenderStatus = async (campaignId) => {
  const res = await axios.get(`${API}/campaigns/${campaignId}/render-status`);
  return res.data;
};

export const downloadRenderedVideo = async (renderId) => {
  const res = await axios.get(`${API}/renders/${renderId}/download`, { responseType: "blob" });
  return res.data;
};

export const fetchVoices = async (search) => {
  const res = await axios.get(`${API}/voices`, { params: search ? { search } : {} });
  return res.data.voices;
};

export const updateAppVoice = async (appId, voiceId, voiceName) => {
  const res = await axios.patch(`${API}/apps/${appId}/voice`, { voice_id: voiceId, voice_name: voiceName });
  return res.data;
};

export const fetchAppRenders = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/renders`);
  return res.data;
};

export const deleteRender = async (renderId) => {
  const res = await axios.delete(`${API}/renders/${renderId}`);
  return res.data;
};

export const fetchRenderThumbnail = async (renderId) => {
  const res = await axios.get(`${API}/renders/${renderId}/thumbnail`, { responseType: "blob" });
  return res.data;
};

export const fetchRenderPreview = async (renderId) => {
  const res = await axios.get(`${API}/renders/${renderId}/preview`, { responseType: "blob" });
  return res.data;
};

export const deleteAllAppRenders = async (appId) => {
  const res = await axios.delete(`${API}/apps/${appId}/renders`);
  return res.data;
};

export const renameVoice = async (voiceId, name) => {
  const res = await axios.patch(`${API}/voices/${voiceId}`, { name });
  return res.data;
};

export const deleteVoice = async (voiceId) => {
  const res = await axios.delete(`${API}/voices/${voiceId}`);
  return res.data;
};

export const fetchSessions = async () => {
  const res = await axios.get(`${API}/auth/sessions`);
  return res.data.sessions;
};

export const revokeSession = async (sessionId) => {
  const res = await axios.delete(`${API}/auth/sessions/${sessionId}`);
  return res.data;
};

export const cloneVoice = async (name, file) => {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("file", file);
  const res = await axios.post(`${API}/voices/clone`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
};

export const fetchMonthlyUsage = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/monthly-usage`);
  return res.data;
};

export const uploadCampaignClip = async (campaignId, slot, file) => {
  const formData = new FormData();
  formData.append("slot", slot);
  formData.append("file", file);
  const res = await axios.post(`${API}/campaigns/${campaignId}/upload-clip`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
};

export const deleteCampaignClip = async (campaignId, slot) => {
  const res = await axios.delete(`${API}/campaigns/${campaignId}/upload-clip/${slot}`);
  return res.data;
};

export const uploadCampaignFullVideo = async (campaignId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/campaigns/${campaignId}/upload-full-video`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
};

export const deleteCampaignFullVideo = async (campaignId) => {
  const res = await axios.delete(`${API}/campaigns/${campaignId}/upload-full-video`);
  return res.data;
};

export const createSocialConnectLink = async (appId) => {
  const res = await axios.post(`${API}/apps/${appId}/social/connect-link`);
  return res.data;
};

export const fetchSocialDestinations = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/social/destinations`);
  return res.data;
};

export const disconnectSocialDestination = async (appId, platform) => {
  const res = await axios.post(`${API}/apps/${appId}/social/disconnect`, { platform });
  return res.data;
};

export const checkInstagramConnection = async (appId, pageId) => {
  const res = await axios.post(`${API}/apps/${appId}/social/instagram-check`, { page_id: pageId || undefined });
  return res.data;
};

export const publishRenderToSocial = async (renderId, payload) => {
  const res = await axios.post(`${API}/renders/${renderId}/publish`, payload);
  return res.data;
};

export const fetchRenderSocialPosts = async (renderId) => {
  const res = await axios.get(`${API}/renders/${renderId}/social-posts`);
  return res.data;
};

export const fetchSocialPostStatus = async (postId) => {
  const res = await axios.get(`${API}/social-posts/${postId}/status`);
  return res.data;
};

export const fetchAllSocialPosts = async (params = {}) => {
  const res = await axios.get(`${API}/social-posts`, { params });
  return res.data;
};

export const retrySocialPost = async (postId) => {
  const res = await axios.post(`${API}/social-posts/${postId}/retry`);
  return res.data;
};

export const fetchGrowthLeadSession = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/growth-lead`);
  return res.data;
};

export const addGrowthLeadPin = async (appId, fact) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-lead/pins`, { fact });
  return res.data.pinned_facts;
};

export const deleteGrowthLeadPin = async (appId, pinIndex) => {
  const res = await axios.delete(`${API}/apps/${appId}/growth-lead/pins/${pinIndex}`);
  return res.data.pinned_facts;
};

export const sendGrowthLeadMessage = async (appId, message) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-lead/message`, { message });
  return res.data.reply;
};

export const resetGrowthLeadSession = async (appId) => {
  const res = await axios.delete(`${API}/apps/${appId}/growth-lead`);
  return res.data;
};

export const exportGrowthLeadPdf = async (appId, message) => {
  const res = await axios.post(`${API}/growth-lead/export`, { app_id: appId, message }, { responseType: "blob" });
  return res.data;
};

export const updateAppTargets = async (appId, targetCpa, targetRoas) => {
  const res = await axios.patch(`${API}/apps/${appId}/targets`, { target_cpa: targetCpa, target_roas: targetRoas });
  return res.data;
};

export const updateAppSpendCap = async (appId, dailySpendCap) => {
  const res = await axios.patch(`${API}/apps/${appId}/spend-cap`, { daily_spend_cap: dailySpendCap });
  return res.data;
};

export const fetchBudgetEntries = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/budget-entries`);
  return res.data;
};

export const createBudgetEntry = async (appId, payload) => {
  const res = await axios.post(`${API}/apps/${appId}/budget-entries`, payload);
  return res.data;
};

export const deleteBudgetEntry = async (appId, entryId) => {
  const res = await axios.delete(`${API}/apps/${appId}/budget-entries/${entryId}`);
  return res.data;
};

export const fetchPortfolioBudget = async () => {
  const res = await axios.get(`${API}/portfolio/budget`);
  return res.data;
};

export const fetchGrowthTeamSession = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/growth-team`);
  return res.data;
};

export const sendGrowthTeamMessage = async (appId, message) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-team/message`, { message });
  return res.data;
};

export const resetGrowthTeamSession = async (appId) => {
  const res = await axios.delete(`${API}/apps/${appId}/growth-team`);
  return res.data;
};

export const generateGrowthTeamWeeklyBrief = async (appId) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-team/weekly-brief`);
  return res.data;
};

export const handoffGrowthTeamToVideo = async (appId, sourceText) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-team/handoff-video`, { source_text: sourceText });
  return res.data;
};

export const saveGrowthTeamAso = async (appId, sourceText) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-team/save-aso`, { source_text: sourceText });
  return res.data;
};

export const saveGrowthTeamSequence = async (appId, sourceText) => {
  const res = await axios.post(`${API}/apps/${appId}/growth-team/save-sequence`, { source_text: sourceText });
  return res.data;
};

export const fetchIntegrationsStatus = async () => {
  const res = await axios.get(`${API}/integrations/status`);
  return res.data;
};

export const connectMetaAds = async () => {
  const res = await axios.get(`${API}/integrations/meta/connect`);
  return res.data;
};

export const connectGoogleAds = async () => {
  const res = await axios.get(`${API}/integrations/google-ads/connect`);
  return res.data;
};

export const disconnectAdPlatform = async (provider) => {
  const res = await axios.delete(`${API}/integrations/${provider}`);
  return res.data;
};

export const publishAdCampaign = async (campaignId, provider, adAccountId) => {
  const res = await axios.post(`${API}/ad-campaigns/publish`, { campaign_id: campaignId, provider, ad_account_id: adAccountId });
  return res.data;
};

export const fetchAdCampaigns = async () => {
  const res = await axios.get(`${API}/ad-campaigns`);
  return res.data.ad_campaigns;
};

export const resumeAdCampaign = async (id) => {
  const res = await axios.post(`${API}/ad-campaigns/${id}/resume`);
  return res.data;
};

export const pauseAdCampaign = async (id) => {
  const res = await axios.post(`${API}/ad-campaigns/${id}/pause`);
  return res.data;
};

export const approveScaleAdCampaign = async (id) => {
  const res = await axios.post(`${API}/ad-campaigns/${id}/approve-scale`);
  return res.data;
};

export const fetchAdSpendSummary = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/ad-spend-summary`);
  return res.data;
};

export const fetchClientReportPdf = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/client-report`, { responseType: "blob" });
  return res.data;
};

export const fetchRegenerationHistory = async (appId) => {
  const res = await axios.get(`${API}/apps/${appId}/regeneration-history`);
  return res.data.events;
};

export const fetchNotifications = async () => {
  const res = await axios.get(`${API}/notifications`);
  return res.data;
};

export const markNotificationRead = async (id) => {
  const res = await axios.post(`${API}/notifications/${id}/read`);
  return res.data;
};

export const markAllNotificationsRead = async () => {
  const res = await axios.post(`${API}/notifications/read-all`);
  return res.data;
};

export const replyToComment = async (slug, commentId, replyText) => {
  const res = await axios.post(`${API}/blog/${slug}/comments/${commentId}/reply`, { reply_text: replyText });
  return res.data;
};

export const deleteComment = async (slug, commentId) => {
  const res = await axios.delete(`${API}/blog/${slug}/comments/${commentId}`);
  return res.data;
};

export const runLinkCheckNow = async () => {
  const res = await axios.post(`${API}/admin/link-check/run-now`);
  return res.data;
};

export const fetchLinkCheckResults = async () => {
  const res = await axios.get(`${API}/admin/link-check/results`);
  return res.data;
};

export const fetchGscStatus = async () => {
  const res = await axios.get(`${API}/admin/search-console/status`);
  return res.data;
};

export const getGscConnectUrl = async () => {
  const res = await axios.get(`${API}/admin/search-console/connect`);
  return res.data;
};

export const fetchGscAnalytics = async (days = 28) => {
  const res = await axios.get(`${API}/admin/search-console/analytics`, { params: { days } });
  return res.data;
};

export const fetchBillingPlans = async () => {
  const res = await axios.get(`${API}/billing/plans`);
  return res.data.plans;
};

export const fetchBillingUsage = async () => {
  const res = await axios.get(`${API}/billing/usage`);
  return res.data;
};

export const fetchWalletLedger = async () => {
  const res = await axios.get(`${API}/billing/wallet/ledger`);
  return res.data.entries;
};

export const createPlanCheckout = async (plan) => {
  const res = await axios.post(`${API}/billing/checkout`, { plan });
  return res.data;
};

export const createWalletCheckout = async (amount) => {
  const res = await axios.post(`${API}/billing/wallet/checkout`, { amount });
  return res.data;
};

export const fetchBillingStatus = async (sessionId) => {
  const res = await axios.get(`${API}/billing/status/${sessionId}`);
  return res.data;
};

export const extractFromUrl = async (url) => {
  const res = await axios.post(`${API}/apps/extract-from-url`, { url });
  return res.data;
};

export const editVideoScript = async (campaignId, payload) => {
  const res = await axios.patch(`${API}/campaigns/${campaignId}/video-script`, payload);
  return res.data;
};

export const regenerateHashtags = async (campaignId) => {
  const res = await axios.post(`${API}/campaigns/${campaignId}/regenerate-hashtags`);
  return res.data.hashtags;
};
